import React from 'react';

// KIQ Chat — Fase 5.1 — Paso 3. QA real en producción mostró Markdown crudo
// ("##", "**", "---") literal en la burbuja de KIQ — la respuesta del
// modelo SÍ trae esa sintaxis y el frontend la mostraba tal cual, como
// texto plano, en vez de darle formato.
//
// Este componente es un parser MUY acotado a propósito (Paso 3 del pedido:
// "no convertir a KIQ en un renderer de HTML de propósito general"). Nunca
// usa dangerouslySetInnerHTML, nunca interpreta ni ejecuta HTML del modelo
// — el resultado son elementos React reales, construidos a mano, así que
// cualquier texto (incluido un <script> literal que el modelo escriba) se
// renderiza como texto inerte, jamás como markup ejecutable.
//
// Subconjunto soportado (exactamente el que pide el pedido, nada más):
// párrafos, negrita (**texto**), encabezados cortos (#/##/###, sutiles —
// nunca un encabezado de documento grande dentro de una burbuja de chat),
// viñetas (-/*), listas numeradas (1.), y una línea divisoria (---/___/***,
// renderizada como un separador visual sutil, nunca como texto crudo).
// Cualquier otra sintaxis (enlaces, imágenes, código, itálicas con _, HTML)
// NO se interpreta — se muestra tal cual como texto plano.
function renderizarInline(texto, keyPrefix) {
  const partes = texto.split(/\*\*(.+?)\*\*/g);
  return partes.map((parte, i) =>
    i % 2 === 1
      ? <strong key={`${keyPrefix}-b-${i}`}>{parte}</strong>
      : <React.Fragment key={`${keyPrefix}-t-${i}`}>{parte}</React.Fragment>
  );
}

function esDivisor(linea) {
  return /^(-{3,}|_{3,}|\*{3,})$/.test(linea.trim());
}
function comoEncabezado(linea) {
  const m = linea.match(/^(#{1,6})\s+(.*)$/);
  return m ? { nivel: m[1].length, texto: m[2] } : null;
}
function comoViñeta(linea) {
  const m = linea.match(/^[-*]\s+(.*)$/);
  return m ? m[1] : null;
}
function comoNumerada(linea) {
  const m = linea.match(/^\d+\.\s+(.*)$/);
  return m ? m[1] : null;
}

function construirBloques(texto) {
  const lineas = texto.replace(/\r\n/g, '\n').split('\n');
  const bloques = [];
  let listaActual = null;

  function cerrarLista() {
    if (listaActual) { bloques.push(listaActual); listaActual = null; }
  }

  for (const linea of lineas) {
    if (!linea.trim()) { cerrarLista(); continue; }
    if (esDivisor(linea)) { cerrarLista(); bloques.push({ tipo: 'hr' }); continue; }
    const encabezado = comoEncabezado(linea);
    if (encabezado) { cerrarLista(); bloques.push({ tipo: 'h', ...encabezado }); continue; }
    const viñeta = comoViñeta(linea);
    if (viñeta !== null) {
      if (!listaActual || listaActual.tipo !== 'ul') { cerrarLista(); listaActual = { tipo: 'ul', items: [] }; }
      listaActual.items.push(viñeta);
      continue;
    }
    const numerada = comoNumerada(linea);
    if (numerada !== null) {
      if (!listaActual || listaActual.tipo !== 'ol') { cerrarLista(); listaActual = { tipo: 'ol', items: [] }; }
      listaActual.items.push(numerada);
      continue;
    }
    cerrarLista();
    bloques.push({ tipo: 'p', texto: linea });
  }
  cerrarLista();
  return bloques;
}

export default function KIQMarkdownLite({ texto }) {
  if (typeof texto !== 'string' || !texto.trim()) return null;
  const bloques = construirBloques(texto);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {bloques.map((bloque, i) => {
        const key = `kiq-md-${i}`;
        if (bloque.tipo === 'hr') {
          return <hr key={key} style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '2px 0', opacity: 0.5 }} />;
        }
        if (bloque.tipo === 'h') {
          // Sutil a propósito (Paso 9 del pedido): un poco más de peso que
          // el texto normal, nunca un encabezado de documento grande.
          return <div key={key} style={{ fontSize: '13px', fontWeight: 700 }}>{renderizarInline(bloque.texto, key)}</div>;
        }
        if (bloque.tipo === 'ul') {
          return (
            <ul key={key} style={{ margin: 0, paddingLeft: '18px' }}>
              {bloque.items.map((item, j) => <li key={`${key}-${j}`}>{renderizarInline(item, `${key}-${j}`)}</li>)}
            </ul>
          );
        }
        if (bloque.tipo === 'ol') {
          return (
            <ol key={key} style={{ margin: 0, paddingLeft: '18px' }}>
              {bloque.items.map((item, j) => <li key={`${key}-${j}`}>{renderizarInline(item, `${key}-${j}`)}</li>)}
            </ol>
          );
        }
        return <p key={key} style={{ margin: 0 }}>{renderizarInline(bloque.texto, key)}</p>;
      })}
    </div>
  );
}
